import React, { useRef } from 'react'
import { useState, useEffect } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useNavigate, useLocation } from 'react-router-dom';
import { contactUs } from "../../utils/wellspoken.api";
import { fetchCourses, fetchPricingPlan } from "../../utils/wellspoken.api";
import toast from "react-hot-toast";
import { FaWhatsapp } from "react-icons/fa";

const Contact = () => {
  const [captchaValue, setCaptchaValue] = useState(null);
  const captchaRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [courses, setCourses] = useState([]);
  const [pricing, setPricing] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    type: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [emailTemplate, setEmailTemplate] = useState(null);

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (submitStatus === "success") {
      toast.success("🎉 Thank you! We will contact you shortly.")
    }

    const fetchTemplate = async () => {
      try {
        const res = await fetch("/templates/contactEmail.txt");
        if (!res.ok) throw new Error("Failed to load email template");
        setEmailTemplate(await res.text());
      } catch (err) {
        console.error(err);
      }
    };

    fetchTemplate();
  }, [submitStatus]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!captchaValue) {
      setSubmitStatus("captchaError");
      return;
    }

    if (!emailTemplate) {
      setSubmitStatus("templateError");
      return;
    }

    setIsSubmitting(true);

    try {
      let emailHtml = emailTemplate;
      Object.keys(formData).forEach((key) => {
        emailHtml = emailHtml.replace(
          new RegExp(`{{${key}}}`, "g"),
          formData[key] || ""
        );
      });

      const payload = {
        fromMail: formData.email,
        emailSubject: formData.type || "General Enquiry",
        emailBody: emailHtml,
        entityName: "wellspoken",
      };

      await contactUs(payload);

      setSubmitStatus("success");
      setFormData({
        name: "",
        email: "",
        phone: "",
        type: "",
        message: "",
      });

      captchaRef.current.reset();
      setCaptchaValue(null);
    } catch (err) {
      console.error(err);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 5000);
    }
  };

//   const handleRequestCallback = () => {
//     console.log("callback requested")
//     setFormData((prev) => ({
//       ...prev,
//       type: prev.type || "Request Callback",
//       message:
//         prev.message ||
//         `Callback requested.
// Phone: ${prev.phone || "Not provided"}
// Preferred contact time: Anytime`,
//     }));
//   };


  useEffect(() => {
    fetchCourses()
      .then(setCourses)
      .catch(() => setCourses([]));

    fetchPricingPlan()
      .then(setPricing)
      .catch(() => setPricing([]));
  }, []);

  useEffect(() => {
    if (location.state?.type) {
      setFormData((prev) => ({
        ...prev,
        type: location.state.type,
      }));
    }
  }, [location.state]);

  return (
    <>


      {/* ========== CONTACT FORM SECTION ========== */}
      <section className="relative bg-gradient-to-b from-white via-blue-50 to-indigo-50 py-12 md:py-20 overflow-hidden">

        {/* Background Decoration - Hidden on very small screens to improve performance */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-24 w-64 h-64 md:w-96 md:h-96 bg-gradient-to-br from-blue-300 to-purple-300 rounded-full filter blur-3xl opacity-20" />
          <div className="absolute -bottom-24 -left-24 w-80 h-80 md:w-[500px] md:h-[500px] bg-gradient-to-tr from-indigo-300 to-pink-300 rounded-full filter blur-3xl opacity-20" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Changed to grid-cols-1 by default, lg:grid-cols-2 for desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-start">

            {/* ========== LEFT: FORM ========== */}
            <div className="order-1">
              {/* Header */}
              <div className="mb-6 md:mb-8 text-center lg:text-left">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-lg border-2 border-blue-200 mb-4">
                  <span className="text-lg md:text-xl">📝</span>
                  <span className="text-xs md:text-sm font-bold text-blue-600 uppercase tracking-wider">
                    Contact Form
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">
                  Ready to Start?
                </h2>
                <p className="text-base md:text-lg text-gray-600 max-w-md mx-auto lg:mx-0">
                  Fill out the form and our team will get back to you within 24 hours.
                </p>
              </div>

              {/* Form Card - Adjusted padding for mobile (p-5) to desktop (p-8) */}
              <form
                onSubmit={handleSubmit}
                className="bg-white rounded-2xl md:rounded-3xl border-2 md:border-4 border-blue-100 shadow-2xl p-5 md:p-8 space-y-5 md:space-y-6"
              >
                {/* Full Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-bold text-gray-900 mb-2">
                    Full Name *
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    autoComplete="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full rounded-xl md:rounded-2xl border-2 border-gray-200 px-4 py-3 md:px-5 md:py-4 text-gray-900 font-semibold focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all"
                    placeholder="Enter your full name"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-bold text-gray-900 mb-2">
                    Email Address *
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full rounded-xl md:rounded-2xl border-2 border-gray-200 px-4 py-3 md:px-5 md:py-4 text-gray-900 font-semibold focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all"
                    placeholder="you@example.com"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-bold text-gray-900 mb-2">
                    Phone Number <span className="text-gray-500 font-normal">(optional)</span>
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full rounded-xl md:rounded-2xl border-2 border-gray-200 px-4 py-3 md:px-5 md:py-4 text-gray-900 font-semibold focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all"
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>

                {/* Interest Dropdown */}
                <div>
                  <label htmlFor="type" className="block text-sm font-bold text-gray-900 mb-2">
                    I'm Interested in
                  </label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="w-full rounded-xl md:rounded-2xl border-2 border-gray-200 px-4 py-3 md:px-5 md:py-4 text-gray-900 font-semibold focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all appearance-none"
                  >
                    <option value="">Select your interest</option>
                    <optgroup label="📚 Courses">
                      {courses.map((course) => (
                        <option key={course.courseId} value={course.courseName}>
                          {course.courseName}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="💎 Pricing Plans">
                      {pricing.map((plan) => (
                        <option key={plan.planId} value={plan.planName}>
                          {plan.planName}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </div>

                {/* reCAPTCHA - responsive container */}
                <div className="overflow-x-auto">
                  <ReCAPTCHA
                    ref={captchaRef}
                    sitekey="6LeDFVosAAAAADrPDSXgTSDrviUrQxEBjjsrAX1M"
                    onChange={handleCaptchaChange}
                  />
                  {submitStatus === "captchaError" && (
                    <p className="mt-2 text-xs font-semibold text-red-600 bg-red-50 px-3 py-2 rounded-lg">
                      ⚠️ Please confirm you are not a robot.
                    </p>
                  )}
                </div>

                {/* Submit Buttons - Stack on mobile, side-by-side on sm screens */}
                <div className="flex flex-col sm:flex-row gap-3 md:gap-4 pt-2">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 text-base md:text-lg font-black text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl md:rounded-2xl shadow-xl hover:scale-105 transition-all"
                  >
                    {isSubmitting ? "Submitting..." : "Submit"}
                  </button>

                  {/* <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={handleRequestCallback}
                    className="w-full py-4 text-base md:text-lg font-black text-blue-600 bg-white border-2 md:border-4 border-blue-600 rounded-xl md:rounded-2xl hover:bg-blue-600 hover:text-white transition-all"
                  >
                    {isSubmitting ? "Submitting..." : "Request Callback"}
                  </button> */}

                </div>

                <p className="text-xs md:text-sm text-gray-600 text-center font-semibold">
                  ⏱️ We typically respond within 1–2 working days.
                </p>
              </form>
            </div>

            {/* ========== RIGHT: CONTACT INFO ========== */}
            <div className="space-y-6 order-2 mt-8 lg:mt-0">
              {/* Header */}
              <div className="mb-6 text-center lg:text-left">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-lg border-2 border-purple-200 mb-4">
                  <span className="text-lg md:text-xl">📞</span>
                  <span className="text-xs md:text-sm font-bold text-purple-600 uppercase tracking-wider">
                    Contact Info
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">
                  Reach Us Directly
                </h2>
              </div>

              {/* Contact Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">

                {/* Email Card */}
                <div className="bg-white rounded-2xl border-2 md:border-4 border-blue-100 p-5 shadow-lg hover:shadow-xl transition-all">
                  <div className="flex items-center gap-4">
                    <div className="shrink-0 w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center text-xl shadow-md">
                      📧
                    </div>
                    <div className="min-w-0">
                      <p className="font-black text-gray-900 text-sm md:text-base">Support</p>
                      <a href="mailto:info@wellspoken.co.in" className="text-blue-600 font-bold text-sm md:text-base break-words hover:underline">
                        info@wellspoken.co.in
                      </a>
                    </div>
                  </div>
                </div>

                {/* Phone Card */}
                <div className="bg-white rounded-2xl border-2 md:border-4 border-green-100 p-5 shadow-lg">
                  <div className="flex items-center gap-4">
                    <div className="shrink-0 w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center text-xl shadow-md">
                      📱
                    </div>
                    <div>
                      <p className="font-black text-gray-900 text-sm">Phone</p>
                      <a href="tel:+919810629181" className="text-green-600 font-bold text-sm block hover:underline">
                        +91 9810629181
                      </a>
                    </div>
                  </div>
                </div>

                {/* WhatsApp Card - Full Width on mobile */}
                <div className="sm:col-span-2 lg:col-span-1 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl p-5 shadow-xl">
                  <div className="flex items-center gap-4">
                    <div className="shrink-0 w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-md">
                      <FaWhatsapp className="text-2xl text-green-600" />
                    </div>
                    <div className="text-white">
                      <p className="font-black text-sm">WhatsApp Support</p>
                      <a href="https://wa.me/919810629181" target="_blank" rel="noopener noreferrer" className="font-bold text-sm hover:underline">
                        Chat Now →
                      </a>
                    </div>
                  </div>
                </div>

                {/* Address Card */}
                {/* <div className="sm:col-span-2 lg:col-span-1 bg-white rounded-2xl border-2 md:border-4 border-purple-100 p-5 shadow-lg">
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-xl">
                      📍
                    </div>
                    <address className="not-italic text-gray-700 font-semibold text-sm">
                      <span className="font-black text-gray-900">Office Address</span><br />
                      Knowledge Park, New Delhi, India
                    </address>
                  </div>
                </div> */}

                <button
                  type="button"
                  onClick={() => navigate("/courses")}
                  className="sm:col-span-2 lg:col-span-1 w-full py-4 text-base font-black text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl shadow-lg hover:scale-105 transition-all mt-2"
                >
                  View All Plans →
                </button>
              </div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;




// import React, { useRef } from 'react'
// import { useState, useEffect } from "react";
// import ReCAPTCHA from "react-google-recaptcha";
// import { useNavigate, useLocation } from 'react-router-dom';
// import { contactUs } from "../../utils/wellspoken.api";
// import { fetchCourses, fetchPricingPlan } from "../../utils/wellspoken.api";
// import toast from "react-hot-toast";
// import { FaWhatsapp } from "react-icons/fa";


// const Contact = () => {

//   const [captchaValue, setCaptchaValue] = useState(null);
//   const captchaRef = useRef(null);
//   const navigate = useNavigate();
//   const location = useLocation();

//   const [courses, setCourses] = useState([]);
//   const [pricing, setPricing] = useState([]);

//   const [formData, setFormData] = useState({
//     name: "",
//     email: "",
//     phone: "",
//     type: "",
//     message: "",
//   });

//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [submitStatus, setSubmitStatus] = useState(null);
//   const [emailTemplate, setEmailTemplate] = useState(null);


//   const handleCaptchaChange = (value) => {
//     setCaptchaValue(value);
//   };

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({
//       ...prev,
//       [name]: value,
//     }));
//   };

//   useEffect(() => {
//     if (submitStatus === "success") {
//       toast.success("🎉 Thank you! We will contact you shortly.")
//     }

//     const fetchTemplate = async () => {
//       try {
//         const res = await fetch("/templates/contactEmail.txt");
//         if (!res.ok) throw new Error("Failed to load email template");
//         setEmailTemplate(await res.text());
//       } catch (err) {
//         console.error(err);
//       }
//     };

//     fetchTemplate();
//   }, [submitStatus]);


//   const handleSubmit = async (e) => {
//     console.log("caontact")
//     e.preventDefault();

//     if (!captchaValue) {
//       setSubmitStatus("captchaError");
//       return;
//     }

//     if (!emailTemplate) {
//       setSubmitStatus("templateError");
//       return;
//     }

//     setIsSubmitting(true);

//     try {
//       // Build email body
//       let emailHtml = emailTemplate;
//       Object.keys(formData).forEach((key) => {
//         emailHtml = emailHtml.replace(
//           new RegExp(`{{${key}}}`, "g"),
//           formData[key] || ""
//         );
//       });

//       // Payload (UNCHANGED)
//       const payload = {
//         fromMail: formData.email,
//         emailSubject: formData.type || "General Enquiry",
//         emailBody: emailHtml,
//         entityName: "wellspoken",
//       };

//       // 🔥 SINGLE API CALL (reused util)
//       await contactUs(payload);

//       setSubmitStatus("success");
//       setFormData({
//         name: "",
//         email: "",
//         phone: "",
//         type: "",
//         message: "",
//       });

//       captchaRef.current.reset();
//       setCaptchaValue(null);
//     } catch (err) {
//       console.error(err);
//       setSubmitStatus("error");
//     } finally {
//       setIsSubmitting(false);
//       setTimeout(() => setSubmitStatus(null), 5000);
//     }
//   };


//   // fetching api
//   useEffect(() => {
//     fetchCourses()
//       .then(setCourses)
//       .catch(() => setCourses([]));

//     fetchPricingPlan()
//       .then(setPricing)
//       .catch(() => setPricing([]));
//   }, []);
//   useEffect(() => {
//     if (location.state?.type) {
//       setFormData((prev) => ({
//         ...prev,
//         type: location.state.type,
//       }));
//     }
//   }, [location.state]);

//   return (
//     <section
//       className="
//       relative overflow-hidden
//       bg-surface py-14 sm:py-20 font-sans
//     "
//       aria-labelledby="contact-heading"
//     >
//       {/* ===== Background Accent ===== */}
//       <div
//         aria-hidden="true"
//         className="
//         pointer-events-none absolute inset-0
//         bg-[radial-gradient(circle_at_10%_20%,rgba(99,102,241,0.08),transparent_40%)]
//       "
//       />

//       <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
//         <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-12 items-start">

//           {/* ================= LEFT: FORM ================= */}
//           <div
//             className="
//             max-w-xl
//             motion-safe:animate-fade-up
//             motion-reduce:animate-none
//           "
//           >
//             <header className="mb-6">
//               <h2
//                 id="contact-heading"
//                 className="text-2xl sm:text-3xl font-bold text-textPrimary"
//               >
//                 Ready to start?
//               </h2>
//               <p className="mt-2 text-textSecondary">
//                 Have questions about courses, pricing, or enrollment?
//                 Fill out the form and our team will get back to you.
//               </p>
//             </header>

//             <form
//               onSubmit={handleSubmit}
//               className="
//               bg-white rounded-2xl border border-border
//               shadow-soft p-6 sm:p-8 space-y-4
//             "
//               aria-label="Contact form"
//               noValidate
//             >
//               {/* Full Name */}
//               <div>
//                 <label
//                   htmlFor="name"
//                   className="block text-sm font-medium text-textPrimary"
//                 >
//                   Full Name
//                 </label>
//                 <input
//                   id="name"
//                   name="name"
//                   type="text"
//                   required
//                   autoComplete="name"
//                   value={formData.name}
//                   onChange={handleChange}
//                   className="
//                   mt-1 w-full rounded-lg border border-border
//                   px-4 py-2.5 text-textPrimary
//                   placeholder:text-textSecondary
//                   transition
//                   focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
//                 "
//                   placeholder="Your full name"
//                 />
//               </div>

//               {/* Email */}
//               <div>
//                 <label
//                   htmlFor="email"
//                   className="block text-sm font-medium text-textPrimary"
//                 >
//                   Email Address
//                 </label>
//                 <input
//                   id="email"
//                   name="email"
//                   type="email"
//                   required
//                   autoComplete="email"
//                   value={formData.email}
//                   onChange={handleChange}
//                   className="
//                   mt-1 w-full rounded-lg border border-border
//                   px-4 py-2.5 text-textPrimary
//                   placeholder:text-textSecondary
//                   transition
//                   focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
//                 "
//                   placeholder="you@example.com"
//                 />
//               </div>

//               {/* Phone */}
//               <div>
//                 <label
//                   htmlFor="phone"
//                   className="block text-sm font-medium text-textPrimary"
//                 >
//                   Phone Number{" "}
//                   <span className="text-textSecondary">(optional)</span>
//                 </label>
//                 <input
//                   id="phone"
//                   name="phone"
//                   type="tel"
//                   autoComplete="tel"
//                   value={formData.phone}
//                   onChange={handleChange}
//                   className="
//                   mt-1 w-full rounded-lg border border-border
//                   px-4 py-2.5 text-textPrimary
//                   placeholder:text-textSecondary
//                   transition
//                   focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
//                 "
//                   placeholder="+91 XXXXX XXXXX"
//                 />
//               </div>

//               {/* User Type */}
//               <div>
//                 <label
//                   htmlFor="type"
//                   className="block text-sm font-medium text-textPrimary"
//                 >
//                   I'm Interested in
//                 </label>
//                 <select
//                   value={formData.type}
//                   onChange={(e) =>
//                     setFormData({ ...formData, type: e.target.value })
//                   }
//                   className="
//                   mt-1 w-full rounded-lg border border-border
//                   px-4 py-2.5 text-textPrimary
//                   transition
//                   focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
//                 "
//                 >
//                   <option value="">Select your interest</option>

//                   <optgroup label="Courses">
//                     {courses.map((course) => (
//                       <option
//                         key={course.courseId}
//                         value={course.courseName}
//                       >
//                         {course.courseName}
//                       </option>
//                     ))}
//                   </optgroup>

//                   <optgroup label="Pricing Plans">
//                     {pricing.map((plan) => (
//                       <option
//                         key={plan.planId}
//                         value={plan.planName}
//                       >
//                         {plan.planName}
//                       </option>
//                     ))}
//                   </optgroup>
//                 </select>
//               </div>
//               {/* ===== CAPTCHA (ACCESSIBLE PLACEMENT) ===== */}
//               <div className="mt-4" aria-live="polite">
//                 <ReCAPTCHA
//                   ref={captchaRef}
//                   sitekey="6LcO490rAAAAAN0Z8WFb17BbwQnQDhESILtoVio0"
//                   onChange={handleCaptchaChange}
//                 />
//                 {submitStatus === "captchaError" && (
//                   <p className="mt-1 text-sm text-red-600">
//                     Please confirm you are not a robot.
//                   </p>
//                 )}
//               </div>
//               {/* CTA */}
//               <div className="mt-6 flex flex-col sm:flex-row gap-3">
//                 <button
//                   type="submit"
//                   disabled={isSubmitting}
//                   aria-busy={isSubmitting}
//                   className="
//                   flex-1 px-6 py-3 rounded-full
//                   bg-primary text-white font-semibold
//                   transition-transform duration-300
//                   hover:bg-primary-dark hover:-translate-y-0.5
//                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
//                   disabled:opacity-60 disabled:transform-none
//                 "
//                 >
//                   {isSubmitting ? "Submitting..." : "Start Free Trial"}
//                 </button>

//                 <button
//                   type="button"
//                   disabled={isSubmitting}
//                   aria-busy={isSubmitting}
//                   className="
//                   flex-1 px-6 py-3 rounded-full
//                   border border-primary text-primary font-semibold
//                   transition
//                   hover:bg-primary/10
//                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
//                 "
//                 >
//                   {isSubmitting ? "Submitting..." : " Request Callback"}

//                 </button>
//               </div>

//               <p className="text-xs text-textSecondary">
//                 We typically respond within 1–2 working days.
//               </p>
//             </form>
//           </div>

//           {/* ================= RIGHT: CONTACT INFO ================= */}
//           <aside
//             aria-label="Contact information"
//             className="
//             bg-white rounded-2xl border border-border
//             shadow-soft p-6 sm:p-8 space-y-6
//             motion-safe:animate-fade-up motion-safe:delay-100
//           "
//           >
//             <h3 className="text-xl font-semibold text-textPrimary">
//               Reach Us Directly
//             </h3>

//             <div className="space-y-5 text-sm text-textSecondary">
//               <div>
//                 <p className="font-medium text-textPrimary">Academic Support</p>
//                 <a
//                   href="mailto:ginalal@hotmail.com"
//                   className="hover:text-primary focus-visible:ring-2 focus-visible:ring-primary"
//                 >
//                   ginalal@hotmail.com
//                 </a>
//               </div>

//               <div>
//                 <p className="font-medium text-textPrimary">Phone</p>
//                 <a
//                   href="tel:+919810629181"
//                   className="hover:text-primary focus-visible:ring-2 focus-visible:ring-primary"
//                 >
//                   +91 9810629181
//                 </a>
//                 <p className="text-xs">Mon – Fri, 9:00 AM – 6:00 PM</p>
//               </div>

//               <div>
//                 <p className="font-medium text-textPrimary">Office Address</p>
//                 <address className="not-italic">
//                   Well Spoken<br />
//                   Knowledge Park, New Delhi, India
//                 </address>
//               </div>

//               <button
//                 type="button"
//                 onClick={() => navigate("/courses")}
//                 className="
//                 w-full px-6 py-3 rounded-full
//                 border border-primary text-primary font-semibold
//                 transition
//                 hover:bg-primary/10
//                 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
//               "
//               >
//                 View Plans
//               </button>

//               <div>
//                 <p className="font-medium text-textPrimary">WhatsApp Support</p>

//                 <a
//                   href="https://wa.me/919810629181"
//                   target="_blank"
//                   rel="noopener noreferrer"
//                   className="
//       inline-flex items-center gap-2 mt-1 text-primary
//       hover:underline
//       focus-visible:ring-2 focus-visible:ring-primary
//     "
//                   aria-label="Chat with support on WhatsApp"
//                 >
//                   <FaWhatsapp className="h-4 w-4" aria-hidden />
//                   <span>Chat on WhatsApp</span>
//                 </a>
//               </div>
//             </div>
//           </aside>

//         </div>
//       </div>
//     </section>
//   );

// }

// export default Contact;
